(function (scope) {
  'use strict';
  const groupId = item => item.properties?._builder_upload_id;
  const isCanvas = item => String(item.properties?._builder_version || '').startsWith('canvaspalette-uploadlift-');
  const variantId = item => String(item.variant_id || item.id);
  function photoSourceURL(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'upload.cloudlift.app' || url.username || url.password || url.port ||
        !/^\/s\/49a4f5-2\/[A-Za-z0-9_-]+\.(?:jpe?g|png|webp|heic|heif)$/i.test(url.pathname)) throw Error('edit_failed');
    // Cloudlift's public /s/ redirect lacks CORS headers. Its public original
    // storage destination explicitly allows cross-origin reads; preserve all bytes.
    return 'https://storage.googleapis.com/cloudlift-app-cloud-prod-upload/' + url.pathname.slice(3);
  }
  function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
    return value;
  }
  function signature(items) {
    return JSON.stringify(items.map(item => ({id: variantId(item), quantity: item.quantity, properties: canonical(item.properties || {})})).sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  }
  function group(cart, id) {
    const items = cart.items.filter(item => groupId(item) === id);
    const parents = items.filter(isCanvas);
    if (parents.length !== 1) throw Error('invalid_group');
    return {id, items, parent: parents[0], signature: signature(items)};
  }
  function validateGroup(value) {
    const {parent, items} = value;
    let crop;
    try { crop = JSON.parse(parent.properties._crop_instructions); } catch (_) { throw Error('invalid_group'); }
    const expected = [String(crop.canvas_variant), ...[crop.frame_variant,crop.coating_variant].filter(Boolean).map(String)].sort();
    if (expected.length !== items.length || expected.some((id,index) => id !== items.map(variantId).sort()[index]) ||
        items.some(item => item.quantity !== parent.quantity) || parent.quantity < 1 ||
        ['Order proof','Wrap layout'].some(name => !parent.properties[name])) throw Error('invalid_group');
    if (parent.properties['_Source kind'] === 'studio') {
      if (['_studio_artwork_id','_studio_order_ref'].some(name => !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parent.properties[name] || '')) ||
          crop.production?.source !== 'private-studio' || !parent.properties['_Production source']) throw Error('invalid_group');
    } else if (['Original photo','_Production files'].some(name => !parent.properties[name])) throw Error('invalid_group');
    return crop;
  }
  function validateCart(cart) {
    for (const item of cart.items.filter(isCanvas)) {
      validateGroup(group(cart,groupId(item)));
      if (item.properties._builder_replaces && cart.items.some(old => groupId(old) === item.properties._builder_replaces)) throw Error('unfinished_edit');
    }
    for (const item of cart.items) {
      if (groupId(item) && (item.properties['Frame upgrade for'] || item.properties['Varnish upgrade for']) && !cart.items.some(parent => isCanvas(parent) && groupId(parent) === groupId(item))) throw Error('invalid_group');
    }
    return true;
  }
  function create({request = scope.fetch.bind(scope), root = scope.Shopify?.routes?.root || '/'} = {}) {
    async function call(path, body) {
      const response = await request(root + path, {credentials:'same-origin',cache:'no-store',...(body ? {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)} : {})});
      const result = await response.json();
      if (!response.ok) throw Error('request_failed');
      return result;
    }
    const read = () => call('cart.js');
    function sameGroup(cart, snapshot) {
      const current = group(cart,snapshot.id);
      if (current.signature !== snapshot.signature) throw Error('cart_changed');
      return current;
    }
    async function removeKnown(id) {
      const cart = await read(), items = cart.items.filter(item => groupId(item) === id);
      if (items.length) {
        try { await call('cart/update.js',{updates:Object.fromEntries(items.map(item => [item.key,0]))}); } catch (_) {}
      }
      const after = await read();
      if (after.items.some(item => groupId(item) === id)) throw Error('unfinished_edit');
      return after;
    }
    async function commit(items, snapshot) {
      const id = groupId(items.find(isCanvas) || {});
      if (!id || items.some(item => groupId(item) !== id)) throw Error('invalid_group');
      validateGroup(group({items},id));
      const before = await read();
      if (before.items.some(item => groupId(item) === id)) throw Error('cart_changed');
      if (snapshot) sameGroup(before,snapshot);
      let addError;
      try { await call('cart/add.js',{items}); } catch (error) { addError = error; }
      const added = await read();
      try {
        const actual = group(added,id); validateGroup(actual);
        if (actual.signature !== signature(items)) throw Error('invalid_group');
        if (snapshot) sameGroup(added,snapshot);
      } catch (error) {
        await removeKnown(id);
        throw Error(error.message === 'cart_changed' ? 'cart_changed' : addError?.message || 'request_failed');
      }
      if (snapshot) {
        const old = sameGroup(added,snapshot);
        try { await call('cart/update.js',{updates:Object.fromEntries(old.items.map(item => [item.key,0]))}); } catch (_) {}
        const after = await read();
        const remaining = after.items.filter(item => groupId(item) === snapshot.id);
        if (remaining.length) {
          if (signature(remaining) === snapshot.signature) { await removeKnown(id); throw Error('request_failed'); }
          throw Error('unfinished_edit');
        }
        validateGroup(group(after,id));
        return after;
      }
      return added;
    }
    async function setQuantity(id, quantity, expected) {
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 999) throw Error('invalid_quantity');
      const before = await read(), current = group(before,id);
      if (expected && current.signature !== expected) throw Error('cart_changed');
      if (quantity > 0) validateGroup(current);
      try { await call('cart/update.js',{updates:Object.fromEntries(current.items.map(item => [item.key,quantity]))}); } catch (_) {}
      const after = await read(), remaining = after.items.filter(item => groupId(item) === id);
      if (!quantity && !remaining.length) return after;
      if (!quantity || remaining.length !== current.items.length || remaining.some(item => item.quantity !== quantity)) throw Error('request_failed');
      validateGroup(group(after,id));
      return after;
    }
    return {read,commit,setQuantity,removeKnown};
  }
  const api = {groupId,isCanvas,group,validateGroup,validateCart,signature,photoSourceURL,create};
  scope.CanvasPaletteCart = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document) return;
  const exclusive = work => scope.navigator?.locks ? scope.navigator.locks.request('canvaspalette-cart',work) : work();
  let busy = false, checkoutReady = false;
  const config = () => document.querySelector('[data-cp-cart-config]');
  function messages() { return JSON.parse(config()?.textContent || '{}'); }
  function announce(key) {
    const output = document.querySelector('[data-cp-cart-status]');
    if (output) { output.hidden=false; output.textContent=messages()[key] || messages().request_failed || key; }
  }
  function lock(value) {
    busy=value;
    document.querySelectorAll('[data-cp-cart] button,[data-cp-cart] input,[name="checkout"]').forEach(el => {
      if (value) { el.dataset.cpDisabled=String(el.disabled);el.disabled=true; }
      else if (el.dataset.cpDisabled !== undefined) {el.disabled=el.dataset.cpDisabled==='true';delete el.dataset.cpDisabled;}
    });
  }
  function enhance() {
    const host=config(); if(!host)return;
    let cart; try { cart=JSON.parse(document.querySelector('[data-cp-cart-data]').textContent); validateCart(cart); } catch(error) { announce(error.message); }
    document.querySelectorAll('[data-cp-group]').forEach(card => {
      try {card.dataset.cpSignature=group(cart,card.dataset.cpGroup).signature;} catch(_) {}
    });
    const state=new URL(location.href).searchParams.get('cp_result'); if(state==='updated')announce('updated');
  }
  document.addEventListener('click',event => {
    const link=event.target.closest('a[data-cp-builder-link]');
    if(link){const url=new URL(link.href);const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);link.href=url.href;}
    const button=event.target.closest('[data-cp-action]'); if(!button)return;
    event.preventDefault();if(busy)return;
    const card=button.closest('[data-cp-group]');if(!card)return;
    const quantity=button.dataset.cpAction==='remove'?0:Number(card.dataset.cpQuantity)+(button.dataset.cpAction==='increase'?1:-1);
    lock(true);announce('updating');
    exclusive(async()=>{
      try {await create().setQuantity(card.dataset.cpGroup,quantity,card.dataset.cpSignature);location.reload();}
      catch(error){announce(error.message);lock(false);}
    });
  },true);
  document.addEventListener('submit',event => {
    const form=event.target;if(!config() || !form.matches('form.cart-page') || event.submitter?.name!=='checkout' || checkoutReady)return;
    event.preventDefault();event.stopImmediatePropagation();if(busy)return;
    const submitter=event.submitter;lock(true);announce('checking');
    exclusive(async()=>{
      try {validateCart(await create().read());checkoutReady=true;lock(false);form.querySelectorAll('[name="updates[]"]').forEach(input=>{input.disabled=true;});form.requestSubmit(submitter);}
      catch(error){announce(error.message);lock(false);}
    });
  },true);
  document.addEventListener('cart:change',()=>{checkoutReady=false;setTimeout(enhance,0);});
  document.addEventListener('shopify:section:load',enhance);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',enhance);else enhance();
})(typeof window === 'undefined' ? globalThis : window);
